import React from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { useNavigate } from "react-router-dom";
import CaptureIntro from "./components/CaptureIntro";
import Capture from "./components/Capture";
import ProfileHome from "./components/ProfileHome";
import Review from "./components/Review";
import HowItWorks from "./components/HowItWorks";
import {
  submitTranscripts,
  getMatchmakerProfile,
  getTraits,
  deleteTrait,
  startMatchmakerProfile,
} from "../../utilities/aiMatchmaker";
import { getSocket } from "../../utilities/socketService";

const INTRO_SEEN_KEY = "matchProfileIntroSeen";

const hasSeenIntro = () => {
  try {
    return window.localStorage.getItem(INTRO_SEEN_KEY) === "true";
  } catch (e) {
    return false;
  }
};

const markIntroSeen = () => {
  try {
    window.localStorage.setItem(INTRO_SEEN_KEY, "true");
  } catch (e) {}
};

const AIMatchmaker = () => {
  const navigate = useNavigate();
  const startedAtCapture = React.useRef(hasSeenIntro());
  const startMarkedRef = React.useRef(false);
  const [step, setStep] = React.useState("loading");
  const [profile, setProfile] = React.useState(null);
  const [traits, setTraits] = React.useState({ self: [], partner: [] });
  const [reviewTab, setReviewTab] = React.useState("me");
  const [cameFromHome, setCameFromHome] = React.useState(false);
  const [showHelp, setShowHelp] = React.useState(false);

  const [aboutMe, setAboutMe] = React.useState("");
  const [lookingFor, setLookingFor] = React.useState("");
  const [meStatus, setMeStatus] = React.useState("idle");
  const [lookStatus, setLookStatus] = React.useState("idle");
  const [meTranscriptId, setMeTranscriptId] = React.useState(null);
  const [lookTranscriptId, setLookTranscriptId] = React.useState(null);
  const [errorMsg, setErrorMsg] = React.useState(null);

  React.useEffect(() => {
    load();
  }, []);

  React.useEffect(() => {
    if (step === "capture" && !startMarkedRef.current) {
      startMarkedRef.current = true;
      startMatchmakerProfile();
    }
  }, [step]);

  React.useEffect(() => {
    const onReady = (data) => {
      const which =
        data && data.promptType === "partner_preference" ? "look" : "me";
      const setStatus = which === "me" ? setMeStatus : setLookStatus;
      if (data && data.status === "failed") {
        setStatus("idle");
        setErrorMsg("We couldn't process that note. Please try again.");
        return;
      }
      setStatus("done");
      if (which === "me") setMeTranscriptId(data.transcriptId);
      else setLookTranscriptId(data.transcriptId);
      refreshProfile();
    };

    let attached = null;
    const attach = (s) => {
      s.on("matchProfileReady", onReady);
      attached = s;
    };

    const existing = getSocket();
    let intervalId;
    if (existing) {
      attach(existing);
    } else {
      intervalId = setInterval(() => {
        const s = getSocket();
        if (s) {
          attach(s);
          clearInterval(intervalId);
          intervalId = null;
        }
      }, 1000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (attached) attached.off("matchProfileReady", onReady);
    };
  }, []);

  const refreshProfile = async () => {
    let p;
    try {
      p = await getMatchmakerProfile();
    } catch (e) {
      p = null;
    }
    if (!p || !p.success) return;
    setProfile(p);
    if (p.hasProfile) {
      let t;
      try {
        t = await getTraits();
      } catch (e) {
        t = null;
      }
      if (t && t.success) {
        setTraits({ self: t.self || [], partner: t.partner || [] });
      }
    }
  };

  const load = async () => {
    setStep("loading");
    let p;
    try {
      p = await getMatchmakerProfile();
    } catch (e) {
      p = null;
    }
    if (p && p.success && p.hasProfile) {
      let t;
      try {
        t = await getTraits();
      } catch (e) {
        t = null;
      }
      setProfile(p);
      setTraits(
        t && t.success
          ? { self: t.self || [], partner: t.partner || [] }
          : { self: [], partner: [] }
      );
      setStep("home");
    } else {
      setProfile(p && p.success ? p : { hasProfile: false });
      setStep(hasSeenIntro() ? "capture" : "intro");
    }
  };

  const startCapture = () => {
    markIntroSeen();
    setStep("capture");
  };

  const addNote = () => {
    setCameFromHome(true);
    setAboutMe("");
    setLookingFor("");
    setMeStatus("idle");
    setLookStatus("idle");
    setMeTranscriptId(null);
    setLookTranscriptId(null);
    setStep("capture");
  };

  const openReview = (which) => {
    setReviewTab(which === "partner" ? "look" : "me");
    setStep("review");
  };

  const submitPrompt = async (which) => {
    const setStatus = which === "me" ? setMeStatus : setLookStatus;
    const payload =
      which === "me"
        ? { aboutMe, lookingFor: "" }
        : { aboutMe: "", lookingFor };
    setStatus("submitting");
    let result;
    try {
      result = await submitTranscripts(payload);
    } catch (e) {
      result = null;
    }
    if (result && result.success) {
      setStatus("processing");
    } else {
      setStatus("idle");
      setErrorMsg(
        (result && result.error) ||
          "Something went wrong submitting your answers. Please try again."
      );
    }
  };

  const handleDelete = async (promptType, id) => {
    let res;
    try {
      res = await deleteTrait({ id, promptType });
    } catch (e) {
      res = null;
    }
    if (res && res.success) {
      setTraits((prev) => {
        const isSelf = promptType === "self_perception";
        return {
          self: isSelf ? prev.self.filter((t) => t.id !== id) : prev.self,
          partner: isSelf
            ? prev.partner
            : prev.partner.filter((t) => t.id !== id),
        };
      });
    } else {
      setErrorMsg((res && res.error) || "Could not delete that. Please try again.");
    }
  };

  const backToHome = cameFromHome || (profile && profile.hasProfile);
  const captureBack = () => {
    if (backToHome) return setStep("home");
    if (startedAtCapture.current) return navigate("/profile");
    return setStep("intro");
  };
  const captureDone = () => {
    if (backToHome) return setStep("home");
    return navigate("/profile");
  };

  const limitReached = !!(profile && profile.limitReached);

  const meChips = meTranscriptId
    ? traits.self.filter((t) => String(t.transcript) === String(meTranscriptId))
    : [];
  const lookChips = lookTranscriptId
    ? traits.partner.filter(
        (t) => String(t.transcript) === String(lookTranscriptId)
      )
    : [];

  let screen;
  if (step === "loading") {
    screen = (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  } else if (step === "intro") {
    screen = <CaptureIntro onStart={startCapture} />;
  } else if (step === "home") {
    screen = (
      <ProfileHome
        profile={profile}
        traits={traits}
        onAddNote={addNote}
        onReviewAll={openReview}
      />
    );
  } else if (step === "review") {
    screen = (
      <Review
        traits={traits}
        onDelete={handleDelete}
        onBack={() => setStep("home")}
        initialTab={reviewTab}
      />
    );
  } else {
    screen = (
      <Capture
        aboutMe={aboutMe}
        lookingFor={lookingFor}
        setAboutMe={setAboutMe}
        setLookingFor={setLookingFor}
        meStatus={meStatus}
        lookStatus={lookStatus}
        meChips={meChips}
        lookChips={lookChips}
        limitReached={limitReached}
        onDeleteTrait={handleDelete}
        onSubmitPrompt={submitPrompt}
        onBack={captureBack}
        onDone={captureDone}
      />
    );
  }

  return (
    <>
      {step !== "loading" ? (
        <IconButton
          onClick={() => setShowHelp(true)}
          aria-label="How Matchmaker works"
          size="small"
          sx={{
            position: "fixed",
            top: 10,
            right: 10,
            zIndex: 1200,
            color: "text.secondary",
            "&:hover": { bgcolor: "transparent" },
          }}
        >
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      ) : null}
      {screen}
      <HowItWorks open={showHelp} onClose={() => setShowHelp(false)} />
      <Dialog
        open={!!errorMsg}
        onClose={() => setErrorMsg(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent>
          <Typography sx={{ fontSize: 15, color: "text.primary" }}>
            {errorMsg}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setErrorMsg(null)} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AIMatchmaker;
